import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, ShieldCheck, ShieldAlert, Pin, PinOff, RotateCcw, TrendingDown, Check,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import { formatDay } from '../../../lib/overOrder';

const MODE_LABEL = {
  auto: 'Left to the rule',
  pinned_on: 'Pinned on',
  pinned_off: 'Pinned off',
};

/**
 * One client at a time: whether their orders are held, why, the allowance
 * their ceiling uses, and the products whose sell-through keeps failing — with
 * the standing quantity behind each one, because a single order corrected
 * today re-appears next week if the template is untouched.
 */
export const ClientsTab = ({ active = true }) => {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [gating, setGating] = useState(null);
  const [loadingGating, setLoadingGating] = useState(false);
  const [failures, setFailures] = useState(null);
  const [loadingFailures, setLoadingFailures] = useState(false);
  const [picked, setPicked] = useState({});
  const [allowance, setAllowance] = useState('');
  const [pinNote, setPinNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!active || clients.length) return;
    (async () => {
      try {
        const list = await adminService.getClients();
        setClients(list || []);
      } catch {
        toast.error('Failed to load clients');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const loadClient = useCallback(async (id) => {
    if (!id) return;
    setLoadingGating(true);
    setLoadingFailures(true);
    setPicked({});
    try {
      const g = await adminService.getClientGating(id);
      setGating(g);
      setAllowance(g.allowance_is_override ? String(g.allowance_pct) : '');
      setPinNote('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load this client');
      setGating(null);
    } finally {
      setLoadingGating(false);
    }
    try {
      setFailures(await adminService.getPersistentFailures(id));
    } catch {
      setFailures(null);
    } finally {
      setLoadingFailures(false);
    }
  }, []);

  useEffect(() => { if (clientId) loadClient(clientId); }, [clientId, loadClient]);

  const setPin = async (mode) => {
    setBusy(true);
    try {
      const res = await adminService.setClientGating(clientId, { mode, note: pinNote || null });
      setGating(res);
      setPinNote('');
      toast.success(
        mode === 'auto'
          ? 'Handed back to the rule'
          : `Approval ${mode === 'pinned_on' ? 'switched on' : 'switched off'} for this client`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change this client');
    } finally {
      setBusy(false);
    }
  };

  const saveAllowance = async (clear = false) => {
    setBusy(true);
    try {
      const value = clear ? null : Number(allowance);
      const res = await adminService.setClientAllowance(clientId, value);
      setGating(res);
      setAllowance(res.allowance_is_override ? String(res.allowance_pct) : '');
      toast.success(clear ? 'Back to the global allowance' : `Allowance set to ${res.allowance_pct}%`);
      loadClient(clientId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save the allowance');
    } finally {
      setBusy(false);
    }
  };

  const reductions = useMemo(
    () => Object.entries(picked)
      .filter(([, v]) => v?.checked && Number(v.quantity) > 0)
      .map(([product_id, v]) => ({ product_id, quantity: Number(v.quantity) })),
    [picked],
  );

  const applyReductions = async () => {
    setBusy(true);
    try {
      const res = await adminService.applyStandingReductions(clientId, reductions);
      toast.success(res.message || 'Standing schedules updated');
      setPicked({});
      loadClient(clientId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply the reductions');
    } finally {
      setBusy(false);
    }
  };

  const togglePick = (row, checked) => {
    setPicked((p) => ({
      ...p,
      [row.product_id]: {
        checked,
        quantity: p[row.product_id]?.quantity ?? (row.suggested_qty ?? row.cap_qty ?? row.standing_qty ?? 1),
      },
    }));
  };

  return (
    <div className="space-y-4" data-testid="oo-clients-tab">
      <div className="max-w-md">
        <Label className="text-sm font-medium text-gray-700">Client</Label>
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger className="mt-1" data-testid="oo-client-select">
            <SelectValue placeholder="Pick a client..." />
          </SelectTrigger>
          <SelectContent>
            {clients.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!clientId ? (
        <Card>
          <CardContent className="py-16 text-center" data-testid="oo-clients-empty">
            <ShieldCheck className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">Pick a client</p>
            <p className="text-sm text-gray-500 mt-1">
              Only guaranteed-returns clients have a ceiling at all.
            </p>
          </CardContent>
        </Card>
      ) : loadingGating ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
        </div>
      ) : !gating ? null : (
        <>
          <Card data-testid="oo-gating-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex flex-wrap items-center gap-2">
                {gating.active ? (
                  <ShieldAlert className="h-5 w-5 text-orange-600" />
                ) : (
                  <ShieldCheck className="h-5 w-5 text-green-600" />
                )}
                Approval is {gating.active ? 'on' : 'off'}
                <Badge
                  className={gating.mode === 'auto' ? 'bg-slate-100 text-slate-700' : 'bg-blue-100 text-blue-800'}
                  data-testid="oo-gating-mode"
                >
                  {MODE_LABEL[gating.mode] || gating.mode}
                </Badge>
                {!gating.is_capped_client && (
                  <Badge className="bg-gray-100 text-gray-600">Not on guaranteed returns</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {gating.reason && (
                <p className="text-sm text-gray-700" data-testid="oo-gating-reason">{gating.reason}</p>
              )}
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
                <span>Breach streak: <span className="font-semibold text-gray-900">{gating.breach_streak}</span></span>
                <span>Clean streak: <span className="font-semibold text-gray-900">{gating.clean_streak}</span></span>
                {gating.since && <span>Since {formatDay(gating.since)}</span>}
                {gating.pinned_by && <span>Pinned by {gating.pinned_by}</span>}
              </div>

              <div>
                <Label className="text-xs font-medium text-gray-700">
                  Note for the pin (optional)
                </Label>
                <Input
                  value={pinNote}
                  onChange={(e) => setPinNote(e.target.value)}
                  placeholder="New site opening — watching it for a month"
                  className="mt-1 max-w-lg text-sm"
                  data-testid="oo-pin-note"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={gating.mode === 'pinned_on' ? 'default' : 'outline'}
                  className={gating.mode === 'pinned_on' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                  disabled={busy}
                  onClick={() => setPin('pinned_on')}
                  data-testid="oo-pin-on-btn"
                >
                  <Pin className="h-4 w-4 mr-2" />
                  Pin on
                </Button>
                <Button
                  size="sm"
                  variant={gating.mode === 'pinned_off' ? 'default' : 'outline'}
                  className={gating.mode === 'pinned_off' ? 'bg-green-600 hover:bg-green-700' : ''}
                  disabled={busy}
                  onClick={() => setPin('pinned_off')}
                  data-testid="oo-pin-off-btn"
                >
                  <PinOff className="h-4 w-4 mr-2" />
                  Pin off
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy || gating.mode === 'auto'}
                  onClick={() => setPin('auto')}
                  data-testid="oo-pin-auto-btn"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Back to the rule
                </Button>
              </div>

              <div className="pt-3 border-t">
                <Label className="text-sm font-medium text-gray-900">Allowance for this client</Label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Their ceiling is their best single delivery times this percentage. Blank uses the
                  global figure ({gating.allowance_is_override ? 'overridden' : `${gating.allowance_pct}%`}).
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <Input
                    type="number"
                    min={100}
                    max={300}
                    value={allowance}
                    onChange={(e) => setAllowance(e.target.value)}
                    placeholder={`${gating.allowance_pct}`}
                    className="w-28 text-sm"
                    data-testid="oo-allowance-input"
                  />
                  <span className="text-sm text-gray-500">%</span>
                  <Button
                    size="sm"
                    disabled={busy || !allowance}
                    onClick={() => saveAllowance(false)}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="oo-allowance-save-btn"
                  >
                    Save
                  </Button>
                  {gating.allowance_is_override && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => saveAllowance(true)}
                      data-testid="oo-allowance-clear-btn"
                    >
                      Use the global figure
                    </Button>
                  )}
                </div>
              </div>

              {gating.history?.length > 0 && (
                <div className="pt-3 border-t">
                  <p className="text-xs font-medium text-gray-700 mb-2">What changed, and when</p>
                  <ul className="space-y-1.5" data-testid="oo-gating-history">
                    {[...gating.history].reverse().map((h, i) => (
                      <li key={`${h.at}-${i}`} className="text-xs text-gray-600">
                        <span className={`font-semibold ${h.active ? 'text-orange-700' : 'text-green-700'}`}>
                          {h.active ? 'Switched on' : 'Switched off'}
                        </span>
                        {' · '}{formatDay(h.at)}{h.by ? ` · ${h.by}` : ''}
                        <span className="block text-gray-500">{h.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="oo-failures-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-orange-600" />
                Products that keep failing
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingFailures ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
                </div>
              ) : !failures?.products?.length ? (
                <p className="text-sm text-gray-500 py-6 text-center" data-testid="oo-failures-empty">
                  Nothing under the {failures?.target_pct ?? 85}% target over the last 90 days.
                </p>
              ) : (
                <>
                  <p className="text-xs text-gray-500 mb-3">
                    Sell-through under {failures.target_pct}% between {formatDay(failures.period.start)} and{' '}
                    {formatDay(failures.period.end)}. Correcting one order changes nothing next week —
                    tick a product to drop the quantity on the client's standing schedules too.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-xs uppercase text-gray-500">
                          <th className="py-2 pr-2 text-left w-8"></th>
                          <th className="py-2 pr-2 text-left">Product</th>
                          <th className="py-2 px-2 text-right">Sold</th>
                          <th className="py-2 px-2 text-right">Back</th>
                          <th className="py-2 px-2 text-right">Cost</th>
                          <th className="py-2 px-2 text-right">Standing</th>
                          <th className="py-2 pl-2 text-right">New standing</th>
                        </tr>
                      </thead>                      <tbody className="divide-y">
                        {failures.products.map((row) => {
                          const pick = picked[row.product_id];
                          return (
                            <tr key={row.product_id} data-testid={`oo-failure-${row.product_id}`}>
                              <td className="py-2 pr-2">
                                <Checkbox
                                  checked={!!pick?.checked}
                                  disabled={!row.standing_qty}
                                  onCheckedChange={(v) => togglePick(row, !!v)}
                                  data-testid={`oo-failure-pick-${row.product_id}`}
                                />
                              </td>
                              <td className="py-2 pr-2">
                                <p className="font-medium text-gray-900">{row.product_name}</p>
                                <p className="text-xs text-gray-500">
                                  {row.sell_through_pct}% sold over {row.deliveries} deliveries
                                  {row.suggested_qty != null ? ` · suggested ${row.suggested_qty}` : ''}
                                  {row.cap_qty != null ? ` · cap ${row.cap_qty}` : ''}
                                </p>
                              </td>
                              <td className="py-2 px-2 text-right tabular-nums">{row.qty_delivered}</td>
                              <td className="py-2 px-2 text-right tabular-nums text-orange-700">{row.qty_returned}</td>
                              <td className="py-2 px-2 text-right tabular-nums">
                                ${((row.credit_given || 0) + (row.waste_cost || 0)).toFixed(2)}
                              </td>
                              <td className="py-2 px-2 text-right tabular-nums">
                                {row.standing_qty ? (
                                  <>
                                    {row.standing_qty}
                                    <span className="block text-xs text-gray-400">
                                      per delivery · {row.standing_schedules} schedule
                                      {row.standing_schedules === 1 ? '' : 's'}
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-gray-400">none</span>
                                )}
                              </td>
                              <td className="py-2 pl-2 text-right">
                                <Input
                                  type="number"
                                  min={1}
                                  disabled={!pick?.checked}
                                  value={pick?.quantity ?? ''}
                                  onChange={(e) => setPicked((p) => ({
                                    ...p,
                                    [row.product_id]: { checked: true, quantity: e.target.value },
                                  }))}
                                  className="h-8 w-20 ml-auto text-right text-sm"
                                  data-testid={`oo-failure-qty-${row.product_id}`}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button
                      className="bg-orange-600 hover:bg-orange-700"
                      disabled={busy || reductions.length === 0}
                      onClick={applyReductions}
                      data-testid="oo-apply-reductions-btn"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Apply to the standing schedule ({reductions.length})
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ClientsTab;
